import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bot, Wand2, Target, ArrowRight, Plus, Settings, Play, Save, Download, Share, Zap, Mail, Globe, BarChart3, Edit3, Trash2 } from 'lucide-react';
import { generateFunnelStrategy } from '../../../services/openai';

const AIFunnelBuilder = ({ selectedCategory, onFunnelCreate, existingFunnel }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [funnelData, setFunnelData] = useState({
    businessType: '',
    targetAudience: '',
    goals: '',
    budget: '',
    timeline: '',
    currentChallenges: ''
  });
  const [aiStrategy, setAiStrategy] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [funnelStages, setFunnelStages] = useState([]);
  const [selectedStage, setSelectedStage] = useState(null);

  useEffect(() => {
    if (selectedCategory) {
      setFunnelData(prev => ({
        ...prev,
        businessType: selectedCategory?.title
      }));
    }
  }, [selectedCategory]);

  const steps = [
    {
      id: 1,
      title: 'Business Information',
      description: 'Tell us about your business and goals',
      icon: Target
    },
    {
      id: 2,
      title: 'AI Strategy Generation',
      description: 'Let AI create your funnel strategy',
      icon: Bot
    },
    {
      id: 3,
      title: 'Funnel Design',
      description: 'Customize your funnel stages',
      icon: Settings
    },
    {
      id: 4,
      title: 'Launch & Optimize',
      description: 'Deploy and monitor performance',
      icon: Play
    }
  ];

  const handleInputChange = (field, value) => {
    setFunnelData(prev => ({ ...prev, [field]: value }));
  };

  const generateAIStrategy = async () => {
    if (!funnelData?.businessType || !funnelData?.targetAudience || !funnelData?.goals) {
      alert('Please fill in all required fields');
      return;
    }

    setIsGenerating(true);
    try {
      const strategy = await generateFunnelStrategy(
        funnelData?.businessType,
        funnelData?.targetAudience,
        funnelData?.goals
      );
      setAiStrategy(strategy);
      setFunnelStages(strategy?.funnel_stages || []);
      setCurrentStep(2);
    } catch (error) {
      console.error('Error generating strategy:', error);
      alert('Failed to generate strategy. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const addCustomStage = () => {
    const newStage = {
      stage: 'Custom Stage',
      goal: 'Define your goal',
      tactics: ['Add your tactics'],
      metrics: ['Add your metrics']
    };
    setFunnelStages(prev => [...prev, newStage]);
  };

  const updateStage = (index, updatedStage) => {
    setFunnelStages(prev => 
      prev?.map((stage, i) => i === index ? updatedStage : stage)
    );
  };

  const removeStage = (index) => {
    setFunnelStages(prev => prev?.filter((_, i) => i !== index));
  };

  const saveFunnel = () => {
    const completeFunnel = {
      ...funnelData,
      strategy: aiStrategy,
      stages: funnelStages,
      createdAt: new Date()?.toISOString(),
      id: Date.now()
    };
    
    localStorage.setItem('currentFunnel', JSON.stringify(completeFunnel));
    onFunnelCreate?.(completeFunnel);
    
    alert('Funnel saved successfully!');
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Tell Us About Your Business</h2>
        <p className="text-gray-600">Provide some details so we can create the perfect funnel for you</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Business Type *
          </label>
          <input
            type="text"
            value={funnelData?.businessType}
            onChange={(e) => handleInputChange('businessType', e?.target?.value)}
            placeholder="e.g., Online Course, Coaching, SaaS"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Target Audience *
          </label>
          <input
            type="text"
            value={funnelData?.targetAudience}
            onChange={(e) => handleInputChange('targetAudience', e?.target?.value)}
            placeholder="e.g., Small business owners, Entrepreneurs"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Business Goals *
          </label>
          <textarea
            value={funnelData?.goals}
            onChange={(e) => handleInputChange('goals', e?.target?.value)}
            placeholder="What do you want to achieve with your funnel? (e.g., Generate 100 leads per month, Increase course sales by 50%)"
            rows={4}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Monthly Budget
          </label>
          <select
            value={funnelData?.budget}
            onChange={(e) => handleInputChange('budget', e?.target?.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="">Select budget range</option>
            <option value="under-1k">Under $1,000</option>
            <option value="1k-5k">$1,000 - $5,000</option>
            <option value="5k-10k">$5,000 - $10,000</option>
            <option value="10k-plus">$10,000+</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Timeline
          </label>
          <select
            value={funnelData?.timeline}
            onChange={(e) => handleInputChange('timeline', e?.target?.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="">Select timeline</option>
            <option value="asap">ASAP (1-2 weeks)</option>
            <option value="month">Within a month</option>
            <option value="quarter">Within 3 months</option>
            <option value="flexible">Flexible timeline</option>
          </select>
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Current Challenges
          </label>
          <textarea
            value={funnelData?.currentChallenges}
            onChange={(e) => handleInputChange('currentChallenges', e?.target?.value)}
            placeholder="What challenges are you currently facing with lead generation or sales?"
            rows={3}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        </div>
      </div>

      <div className="flex justify-center">
        <button
          onClick={generateAIStrategy}
          disabled={isGenerating || !funnelData?.businessType || !funnelData?.targetAudience || !funnelData?.goals}
          className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-medium py-3 px-8 rounded-lg transition-all flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isGenerating ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              <span>Generating Strategy...</span>
            </>
          ) : (
            <>
              <Wand2 className="w-5 h-5" />
              <span>Generate AI Strategy</span>
            </>
          )}
        </button>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">AI-Generated Strategy</h2>
        <p className="text-gray-600">Review and customize your personalized funnel strategy</p>
      </div>

      {aiStrategy && (
        <div className="space-y-6">
          {/* Strategy Overview */}
          <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Strategy Overview</h3>
            <p className="text-gray-700">{aiStrategy?.strategy_overview}</p>
          </div>

          {/* Lead Magnets */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Target className="w-5 h-5 mr-2 text-purple-600" />
              Lead Magnets
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {aiStrategy?.lead_magnets?.map((magnet, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-2">{magnet?.title}</h4>
                  <p className="text-sm text-gray-600 mb-2">{magnet?.description}</p>
                  <span className="inline-block bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded">
                    {magnet?.format}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Automation Sequences */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Zap className="w-5 h-5 mr-2 text-blue-600" />
              Automation Sequences
            </h3>
            <div className="space-y-4">
              {aiStrategy?.automation_sequences?.map((sequence, index) => (
                <div key={index} className="border-l-4 border-blue-500 pl-4">
                  <h4 className="font-medium text-gray-900 mb-1">{sequence?.name}</h4>
                  <p className="text-sm text-gray-600 mb-2">Trigger: {sequence?.trigger}</p>
                  <div className="flex flex-wrap gap-2">
                    {sequence?.sequence?.slice(0, 3)?.map((step, stepIndex) => (
                      <span key={stepIndex} className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                        {step}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Optimization Tips */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <BarChart3 className="w-5 h-5 mr-2 text-green-600" />
              Optimization Tips
            </h3>
            <ul className="space-y-2">
              {aiStrategy?.optimization_tips?.map((tip, index) => (
                <li key={index} className="flex items-start space-x-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-gray-700">{tip}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="flex justify-center space-x-4">
            <button
              onClick={() => setCurrentStep(3)}
              className="bg-purple-600 hover:bg-purple-700 text-white font-medium py-2 px-6 rounded-lg transition-colors flex items-center space-x-2"
            >
              <span>Customize Funnel</span>
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => setCurrentStep(1)}
              className="border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium py-2 px-6 rounded-lg transition-colors"
            >
              Back to Edit
            </button>
          </div>
        </div>
      )}
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Customize Your Funnel</h2>
        <p className="text-gray-600">Edit stages, add custom steps, and fine-tune your funnel</p>
      </div>

      <div className="space-y-4">
        {funnelStages?.map((stage, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-lg border border-gray-200 p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Stage {index + 1}: {stage?.stage}</h3>
              <div className="flex space-x-2">
                <button
                  onClick={() => setSelectedStage(selectedStage === index ? null : index)}
                  className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <Edit3 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => removeStage(index)}
                  className="p-2 text-red-400 hover:text-red-600 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            <AnimatePresence>
              {selectedStage === index && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="space-y-4"
                >
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Stage Name</label>
                    <input
                      type="text"
                      value={stage?.stage}
                      onChange={(e) => updateStage(index, { ...stage, stage: e?.target?.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Goal</label>
                    <textarea
                      value={stage?.goal}
                      onChange={(e) => updateStage(index, { ...stage, goal: e?.target?.value })}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Goal</h4>
                <p className="text-gray-600 text-sm">{stage?.goal}</p>
              </div>
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Key Metrics</h4>
                <div className="flex flex-wrap gap-1">
                  {stage?.metrics?.map((metric, metricIndex) => (
                    <span key={metricIndex} className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">
                      {metric}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-4">
              <h4 className="font-medium text-gray-900 mb-2">Tactics</h4>
              <div className="flex flex-wrap gap-2">
                {stage?.tactics?.map((tactic, tacticIndex) => (
                  <span key={tacticIndex} className="bg-blue-100 text-blue-800 text-sm px-3 py-1 rounded-full">
                    {tactic}
                  </span>
                ))}
              </div>
            </div>
          </motion.div>
        ))}

        <button
          onClick={addCustomStage}
          className="w-full border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-purple-400 hover:bg-purple-50 transition-colors group"
        >
          <Plus className="w-8 h-8 text-gray-400 group-hover:text-purple-600 mx-auto mb-2" />
          <span className="text-gray-600 group-hover:text-purple-600 font-medium">Add Custom Stage</span>
        </button>
      </div>

      <div className="flex justify-center space-x-4">
        <button
          onClick={saveFunnel}
          className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-6 rounded-lg transition-colors flex items-center space-x-2"
        >
          <Save className="w-4 h-4" />
          <span>Save Funnel</span>
        </button>
        <button
          onClick={() => setCurrentStep(4)}
          className="bg-purple-600 hover:bg-purple-700 text-white font-medium py-2 px-6 rounded-lg transition-colors flex items-center space-x-2"
        >
          <span>Launch Funnel</span>
          <Play className="w-4 h-4" />
        </button>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="text-center space-y-6">
      <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-8">
        <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
          <Play className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Funnel Ready to Launch!</h2>
        <p className="text-gray-600 mb-6">Your AI-powered marketing funnel is configured and ready to start generating leads.</p>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <Mail className="w-8 h-8 text-blue-500 mx-auto mb-2" />
            <h3 className="font-semibold text-gray-900">Email Sequences</h3>
            <p className="text-sm text-gray-600">Automated email flows configured</p>
          </div>
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <Globe className="w-8 h-8 text-green-500 mx-auto mb-2" />
            <h3 className="font-semibold text-gray-900">Landing Pages</h3>
            <p className="text-sm text-gray-600">High-converting pages ready</p>
          </div>
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <BarChart3 className="w-8 h-8 text-purple-500 mx-auto mb-2" />
            <h3 className="font-semibold text-gray-900">Analytics</h3>
            <p className="text-sm text-gray-600">Tracking and optimization setup</p>
          </div>
        </div>

        <div className="flex justify-center space-x-4">
          <button className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-6 rounded-lg transition-colors flex items-center space-x-2">
            <Play className="w-4 h-4" />
            <span>Launch Now</span>
          </button>
          <button className="border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium py-2 px-6 rounded-lg transition-colors flex items-center space-x-2">
            <Download className="w-4 h-4" />
            <span>Export</span>
          </button>
          <button className="border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium py-2 px-6 rounded-lg transition-colors flex items-center space-x-2">
            <Share className="w-4 h-4" />
            <span>Share</span>
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto">
      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-center">
          {steps?.map((step, index) => (
            <div key={step?.id} className="flex items-center">
              <div className={`flex flex-col items-center ${index < steps?.length - 1 ? 'mr-8' : ''}`}>
                <div className={`w-12 h-12 rounded-full flex items-center justify-center border-2 ${
                  currentStep >= step?.id 
                    ? 'bg-purple-600 border-purple-600 text-white' :'border-gray-300 text-gray-400'
                }`}>
                  <step.icon className="w-6 h-6" />
                </div>
                <div className="text-center mt-2">
                  <div className={`text-sm font-medium ${
                    currentStep >= step?.id ? 'text-purple-600' : 'text-gray-400'
                  }`}>
                    {step?.title}
                  </div>
                  <div className="text-xs text-gray-500">{step?.description}</div>
                </div>
              </div>
              {index < steps?.length - 1 && (
                <div className={`h-1 w-16 ${
                  currentStep > step?.id ? 'bg-purple-600' : 'bg-gray-300'
                }`} />
              )}
            </div>
          ))}
        </div>
      </div>
      {/* Step Content */}
      <motion.div
        key={currentStep}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        className="bg-white rounded-lg shadow-sm p-8"
      >
        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}
        {currentStep === 3 && renderStep3()}
        {currentStep === 4 && renderStep4()}
      </motion.div>
    </div>
  );
};

export default AIFunnelBuilder;